import React from 'react';
import { Helmet } from 'react-helmet';
import styled, { css } from "styled-components";
import { motion } from "framer-motion";

// Глобальные стили для предотвращения горизонтального скроллинга
const GlobalStyles = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

// Стиль для секции
const Section = styled(motion.section)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f4f4f4;
  color: #333;
  transition: background 0.5s ease;
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// Заголовок
const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 2rem;
  color: #007aff;
  animation: fadeIn 1s ease-in-out;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
`;

// Контейнер для тарифов
const TariffContainer = styled(motion.div)`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 600px;
  margin: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
  @media (max-width: 768px) {
    width: 100%;
    padding: 1.5rem;
  }
`;

// Подзаголовок
const Subtitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: #333;
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.5rem;
  }
`;

// Описание тарифа
const TariffDescription = styled.p`
  font-size: 1rem;
  color: #555;
  margin-bottom: 1rem;
  line-height: 1.5;
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
`;

// Дополнительная информация
const AdditionalInfo = styled.p`
  font-size: 1rem;
  color: #333;
  margin-bottom: 0.5rem;
  line-height: 1.5;
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
`;

const About = () => {
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const tariffVariants = {
    hidden: { opacity: 0, y: 50, scale: 1 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
    hover: { scale: 1.02, transition: { duration: 0.3, ease: "easeInOut" } },
  };

  return (
    <GlobalStyles>
      <Helmet>
        <title>О Компании | Сонет-Дом</title>
        <meta name="description" content="Информация о компании Сонет-Дом. История, основные направления деятельности, контактная информация." />
        <meta name="keywords" content="о компании, история, услуги, система безопасности, Сонет-Дом" />
      </Helmet>
      <Section
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        bgColor="#f4f4f4"
        textColor="#333"
        style={{ marginTop: '60px' }} // Добавляем отступ сверху
      >
        <Title>О Компании</Title>

        <TariffContainer
          variants={tariffVariants}
          initial="hidden"
          whileInView="visible"
          whileHover="hover"
          viewport={{ once: true }}
        >
          <Subtitle>История</Subtitle>
          <TariffDescription>
            Компания «Сонет-Дом» с 05 января 2003 года специализируется на проектировании, монтаже и техническом обслуживании различных систем безопасности, в том числе комплексных систем безопасности и жизнеобеспечения объектов, а также систем телевидения и связи.
          </TariffDescription>
          <TariffDescription>
            При создании охранных комплексов используются последние достижения в области технических средств охраны: телевизионных систем наблюдения, систем охранной и пожарной сигнализации, систем контроля доступа. Это дает возможность создать эффективную многоуровневую систему защиты. Все спроектированные фирмой системы допускают дальнейшее развитие и модернизацию установленного оборудования.
          </TariffDescription>
          <TariffDescription>
            Наша компания оснастила системами безопасности множество объектов - от коттеджей и офисов до многоквартирных жилых домов и государственных учреждений.
          </TariffDescription>
        </TariffContainer>

        <TariffContainer
          variants={tariffVariants}
          initial="hidden"
          whileInView="visible"
          whileHover="hover"
          viewport={{ once: true }}
        >
          <Subtitle>Основные направления деятельности</Subtitle>
          <ul>
            <li>Домофоны, видеодомофоны</li>
            <li>Системы контроля доступа</li>
            <li>Системы видеонаблюдения</li>
          </ul>
        </TariffContainer>

        <TariffContainer
          variants={tariffVariants}
          initial="hidden"
          whileInView="visible"
          whileHover="hover"
          viewport={{ once: true }}
        >
          <Subtitle>Часы работы</Subtitle>
          <div>
            <h3>Солнечногорск</h3>
            <AdditionalInfo>Пн-Чт: 9.00 - 18.00</AdditionalInfo>
            <AdditionalInfo>Пт: 9.00 - 17.00</AdditionalInfo>
            <AdditionalInfo>Обед: 13.00 - 14.00</AdditionalInfo>
            <AdditionalInfo>Сб: 10.00 - 14.00 (дежурный день)</AdditionalInfo>
          </div>
          <div>
            <h3>Клин</h3>
            <AdditionalInfo>Пн-Чт: 9.00 - 17.00</AdditionalInfo>
            <AdditionalInfo>Пт: 9.00 - 17.00</AdditionalInfo>
            <AdditionalInfo>Обед: 13.00 - 14.00</AdditionalInfo>
            <AdditionalInfo>Сб: 10.00 - 14.00 (дежурный день)</AdditionalInfo>
          </div>
        </TariffContainer>
      </Section>
    </GlobalStyles>
  );
};

export default About;